/**
 * StepList — Renders the full list of steps with drag-to-reorder.
 * Uses react-beautiful-dnd for drag support.
 * Composes StepRow (collapsed) + StepDetail (expanded).
 */

import React, { useCallback } from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import StepRow from './StepRow';
import StepDetail from './StepDetail';

const StepList = ({
  steps,
  expandedIndex,
  stepStatuses,
  onToggleExpand,
  onUpdateStep,
  onDeleteStep,
  onDuplicateStep,
  onMoveStep,
  onReorderStep,
  onRunStep,
  onAddStep,
  onPickSelector, // #68: extension-only picker wiring for per-step selectors
}) => {
  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;
      const from = result.source.index;
      const to = result.destination.index;
      if (from !== to) {
        onReorderStep(from, to);
      }
    },
    [onReorderStep]
  );

  if (!steps || steps.length === 0) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Paper
          variant="outlined"
          sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed' }}
        >
          <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
            No steps defined. Click "Add Step" to get started.
          </Typography>
          <Button
            startIcon={<AddIcon />}
            size="small"
            variant="outlined"
            onClick={() => onAddStep('action.navigate')}
          >
            Add Step
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1, py: 1 }}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="unified-step-list">
          {(provided) => (
            <Box ref={provided.innerRef} {...provided.droppableProps}>
              {steps.map((step, idx) => {
                const isExpanded = expandedIndex === idx;
                const status = stepStatuses?.[idx] || 'pending';
                return (
                  <Draggable
                    key={`step-${idx}-${step.stepNumber}`}
                    draggableId={`step-${idx}`}
                    index={idx}
                  >
                    {(dragProvided) => (
                      <Box ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                        <StepRow
                          step={step}
                          index={idx}
                          status={status}
                          isExpanded={isExpanded}
                          onToggleExpand={() => onToggleExpand(idx)}
                          dragHandleProps={dragProvided.dragHandleProps}
                        />
                        {isExpanded && (
                          <StepDetail
                            step={step}
                            index={idx}
                            totalSteps={steps.length}
                            onUpdate={(updated) => onUpdateStep(idx, updated)}
                            onDelete={() => onDeleteStep(idx)}
                            onDuplicate={() => onDuplicateStep(idx)}
                            onMoveUp={() => onMoveStep(idx, -1)}
                            onMoveDown={() => onMoveStep(idx, 1)}
                            onRunStep={() => onRunStep && onRunStep(step, idx)}
                            onPickSelector={onPickSelector}
                          />
                        )}
                      </Box>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      <Button
        startIcon={<AddIcon />}
        size="small"
        sx={{ mt: 1, ml: 1 }}
        variant="outlined"
        onClick={() => onAddStep('action.navigate')}
      >
        Add Step
      </Button>
    </Box>
  );
};

export default StepList;
